import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Descriptions,
  Tag,
  Divider,
  message,
  Button,
  Spin,
  Timeline,
  List,
  Typography,
  Row,
  Col,
} from "antd";
import {
  ArrowLeftOutlined,
  FileTextOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { Case } from "../../../shared/types";
import { api } from "../../services/api";
import { useAuth } from "../../stores/auth";
import dayjs from "dayjs";

const { Text, Title } = Typography;

const statusMap = {
  draft: { color: "default", text: "草稿" },
  pending: { color: "processing", text: "待处理" },
  archived: { color: "success", text: "已归档" },
};

const categoryMap: Record<string, string> = {
  legal_doc: "法律文书",
  decision: "立案决定书",
  opinion: "鉴定意见书",
  photo_a: "相关照片一",
  photo_b: "相关照片二",
  handle_file: "办理附件",
  other: "其他",
};

export default function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchCase();
  }, [id]);

  const fetchCase = async () => {
    try {
      const data = await api.getCase(Number(id));
      setCaseData(data);
    } catch (error: any) {
      message.error(error.message || "获取案件信息失败");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType?.startsWith("image/")) {
      return <FileImageOutlined style={{ color: "#1890ff" }} />;
    }
    if (fileType?.includes("pdf")) {
      return <FilePdfOutlined style={{ color: "#ff4d4f" }} />;
    }
    return <FileOutlined style={{ color: "#666" }} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const handleDownload = (fileKey: string, fileName: string) => {
    const url = api.getFileUrl(fileKey);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (!caseData) {
    return null;
  }

  // 按类别分组附件
  const attachmentsByCategory = (caseData.attachments || []).reduce(
    (acc, att) => {
      if (!acc[att.category]) {
        acc[att.category] = [];
      }
      acc[att.category].push(att);
      return acc;
    },
    {} as Record<string, typeof caseData.attachments>
  );

  return (
    <div className="max-w-5xl mx-auto">
      <Card bordered={false}>
        <div className="mb-6">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/")}
            className="mb-4"
          >
            返回列表
          </Button>
          <div className="flex justify-between items-center">
            <Title level={4} style={{ marginBottom: 0 }}>
              案件详情
            </Title>
            {caseData.status === "pending" && user?.role === "user_b" && (
              <Button
                type="primary"
                onClick={() => navigate(`/cases/${id}/handle`)}
              >
                去办理
              </Button>
            )}
          </div>
        </div>

        <Descriptions bordered column={2} className="mb-6">
          <Descriptions.Item label="案件编号">
            <span className="font-mono">{caseData.case_no}</span>
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={statusMap[caseData.status]?.color}>
              {statusMap[caseData.status]?.text}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="案件名称" span={2}>
            {caseData.case_name}
          </Descriptions.Item>
          <Descriptions.Item label="创建人">
            <UserOutlined className="mr-1" />
            {caseData.creator_name}
          </Descriptions.Item>
          <Descriptions.Item label="指派人">
            {caseData.assignee_name ? (
              <>
                <UserOutlined className="mr-1" />
                {caseData.assignee_name}
              </>
            ) : (
              "-"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">
            <ClockCircleOutlined className="mr-1" />
            {dayjs(caseData.created_at).format("YYYY-MM-DD HH:mm")}
          </Descriptions.Item>
          <Descriptions.Item label="归档时间">
            {caseData.archived_at ? (
              <>
                <ClockCircleOutlined className="mr-1" />
                {dayjs(caseData.archived_at).format("YYYY-MM-DD HH:mm")}
              </>
            ) : (
              "-"
            )}
          </Descriptions.Item>
        </Descriptions>

        {/* 案件情况 */}
        {caseData.case_situation && (
          <Card
            type="inner"
            title="案件情况"
            className="mb-4"
            size="small"
          >
            <p className="whitespace-pre-wrap">{caseData.case_situation}</p>
          </Card>
        )}

        {/* 矛盾化解情况 */}
        {caseData.resolution_info && (
          <Card
            type="inner"
            title="矛盾化解情况"
            className="mb-4"
            size="small"
          >
            <p className="whitespace-pre-wrap">{caseData.resolution_info}</p>
          </Card>
        )}

        {/* 介入情况 */}
        {caseData.intervention_info && (
          <Card
            type="inner"
            title="介入情况"
            className="mb-4"
            size="small"
          >
            <p className="whitespace-pre-wrap">
              {caseData.intervention_info}
            </p>
          </Card>
        )}

        {/* 调解结果 */}
        {caseData.mediation_result && (
          <Card
            type="inner"
            title="调解结果"
            className="mb-4"
            size="small"
          >
            <p className="whitespace-pre-wrap">{caseData.mediation_result}</p>
          </Card>
        )}

        {/* 附件列表 */}
        {Object.keys(attachmentsByCategory).length > 0 && (
          <Card type="inner" title="附件材料" className="mb-4" size="small">
            {Object.entries(attachmentsByCategory).map(
              ([category, attachments]) => (
                <div key={category} className="mb-4 last:mb-0">
                  <Text strong className="block mb-2">
                    {categoryMap[category] || category}
                  </Text>
                  <List
                    size="small"
                    dataSource={attachments}
                    renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Button
                            key="download"
                            type="link"
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() =>
                              handleDownload(item.file_key, item.file_name)
                            }
                          >
                            下载
                          </Button>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={getFileIcon(item.file_type)}
                          title={item.file_name}
                          description={formatFileSize(item.file_size)}
                        />
                      </List.Item>
                    )}
                  />
                </div>
              )
            )}
          </Card>
        )}

        {/* 操作记录 */}
        {caseData.records && caseData.records.length > 0 && (
          <Card type="inner" title="操作记录" className="mb-4" size="small">
            <Timeline
              items={caseData.records.map((record) => ({
                children: (
                  <div>
                    <div className="flex items-center gap-2">
                      <UserOutlined className="text-gray-400" />
                      <span className="font-medium">{record.user_name}</span>
                      <span className="text-gray-400 text-sm">
                        {dayjs(record.created_at).format("YYYY-MM-DD HH:mm")}
                      </span>
                    </div>
                    <div className="mt-1 ml-6">
                      <Tag color="blue">{record.record_type}</Tag>
                    </div>
                    {record.content && (
                      <div className="mt-2 ml-6 p-3 bg-gray-50 rounded">
                        <p className="whitespace-pre-wrap">{record.content}</p>
                      </div>
                    )}
                  </div>
                ),
              }))}
            />
          </Card>
        )}
      </Card>
    </div>
  );
}
